/**
 * ============================================================================
 * USER SERVICE - API CLIENT
 * ============================================================================
 * 
 * Handles all user-related API calls:
 * - Profile management
 * - User statistics
 * - Ratings and reviews
 * - KYC verification
 * - GDPR compliance (data export, account deletion)
 * - User search
 */

import api, { handleApiError } from './api';

/**
 * Get current user profile
 */
export const getMyProfile = async () => {
  try {
    const response = await api.get('/users/me/profile');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get user profile by ID
 */
export const getUserProfile = async (userId) => {
  try {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Update user profile
 */
export const updateProfile = async (data) => {
  try {
    const response = await api.patch('/users/me/profile', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Upload profile photo
 */
export const uploadProfilePhoto = async (file) => {
  try {
    const formData = new FormData();
    formData.append('photo', file);

    const response = await api.post('/users/me/photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Delete profile photo
 */
export const deleteProfilePhoto = async () => {
  try {
    const response = await api.delete('/users/me/photo');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get current user's statistics (authenticated)
 */
export const getMyStats = async () => {
  try {
    const response = await api.get('/users/me/stats');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get user statistics by user ID (public)
 */
export const getUserStats = async (userId) => {
  try {
    const response = await api.get(`/users/${userId}/stats`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get my activity summary
 */
export const getMyActivity = async (period = 'month') => {
  try {
    const response = await api.get('/users/me/activity', {
      params: { period },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get my transactions
 */
export const getMyTransactions = async (filters = {}) => {
  try {
    const response = await api.get('/users/me/transactions', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get user ratings
 */
export const getUserRatings = async (userId, page = 1, limit = 10) => {
  try {
    const response = await api.get(`/users/${userId}/ratings`, {
      params: { page, limit },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get my given ratings
 */
export const getMyGivenRatings = async (page = 1, limit = 10) => {
  try {
    const response = await api.get('/users/me/ratings/given', {
      params: { page, limit },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Submit a rating
 */
export const submitRating = async (data) => {
  try {
    const response = await api.post('/users/me/ratings', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Request KYC verification
 */
export const requestKYCVerification = async (formData) => {
  try {
    const response = await api.post('/users/me/verification/kyc', formData, {
      headers: formData instanceof FormData ? {} : { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get verification status
 */
export const getVerificationStatus = async () => {
  try {
    const response = await api.get('/users/me/verification/status');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Search users
 */
export const searchUsers = async (query, page = 1, limit = 20) => {
  try {
    const response = await api.get('/users/search', {
      params: { q: query, page, limit },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get account settings
 */
export const getSettings = async () => {
  try {
    const response = await api.get('/users/me/settings');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Update account settings
 */
export const updateSettings = async (data) => {
  try {
    const response = await api.patch('/users/me/settings', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Deactivate account
 */
export const deactivateAccount = async (data) => {
  try {
    const response = await api.post('/users/me/deactivate', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Request account deletion (GDPR Article 17)
 */
export const requestAccountDeletion = async (data) => {
  try {
    const response = await api.post('/users/me/delete', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Cancel account deletion
 */
export const cancelAccountDeletion = async () => {
  try {
    const response = await api.post('/users/me/cancel-deletion');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Export user data (GDPR Article 20)
 * @param {Object} options - Export options
 * @param {string} options.format - Export format (default: 'json')
 */
export const exportUserData = async (options = {}) => {
  try {
    const format = options?.format || options || 'json';
    const response = await api.get('/users/me/data-export', {
      params: { format },
    });
    
    // Create download link
    const blob = new Blob([JSON.stringify(response.data.data || response.data, null, 2)], {
      type: 'application/json',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wekatrust-data-export-${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Delete account (GDPR Article 17)
 * @param {Object} options - Deletion options
 * @param {string} options.password - User password (if password is set)
 */
export const deleteAccount = async (options = {}) => {
  try {
    // Handle both object and direct password parameter
    const password = typeof options === 'string' ? options : (options?.password || undefined);
    const response = await api.delete('/users/me/account', {
      data: password ? { password } : {},
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default {
  getMyProfile,
  getUserProfile,
  updateProfile,
  uploadProfilePhoto,
  deleteProfilePhoto,
  getMyStats,
  getUserStats,
  getMyActivity,
  getMyTransactions,
  getUserRatings,
  getMyGivenRatings,
  submitRating,
  requestKYCVerification,
  getVerificationStatus,
  searchUsers,
  getSettings,
  updateSettings,
  deactivateAccount,
  requestAccountDeletion,
  cancelAccountDeletion,
  exportUserData,
  deleteAccount,
};
